import React, {useCallback, useEffect, useLayoutEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";
import {Box, Card, CircularProgress, DialogActions, DialogContent, DialogTitle, Fade, Skeleton, Tooltip, Typography,} from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2";
import {DeleteOutlined, EditOutlined, Star, StarBorder} from "@mui/icons-material";
import classnames from "classnames";
import {useDispatch} from "react-redux";
import Dialog from "../../base/dialog";
import Button from "../../base/button/button";
import IconButton from "../../base/button/icon_button";
import Form, {MuiRFF, Yup} from "../../form";
import SaveOrRollback from "../../app-specific/save-or-rollback";
import ValidateMessages from "../../../../core/models/static/texts/vallidate-messages";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";
import useAccessLevels from "../../../hooks/use-access-levels";
import {setReduxDialogState} from "../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../core/models/static/routes/redux-store-routes";
import './index.scss';

/**
 * Manage partner vendor statuses: create, rename, set default, delete with replacement.
 */
const ManageVendorStatusesDialog = ({open, onClose}) => {
    const {t} = useTranslation('dialogs', {keyPrefix: 'app-specific.manage-vendor-statuses'});
    const [creating, setCreating] = useState(false);
    const [loading, setLoading] = useState(false);
    const [settingDefaultId, setSettingDefaultId] = useState(null);
    const [statuses, setStatuses] = useState([]);
    const isMounted = useIsMounted();
    const {packages} = useAccessLevels();
    const canManageAccountInfo = packages.base.manageVendorAccountInfo;
    const dispatch = useDispatch();

    const busy = useMemo(
        () => creating || !!settingDefaultId || statuses.some((s) => s.editing),
        [creating, settingDefaultId, statuses],
    );

    /**
     * Resets local state when the dialog opens.
     */
    useLayoutEffect(() => {
        if (open) {
            setStatuses([]);
            setLoading(false);
            setCreating(false);
            setSettingDefaultId(null);
        }
    }, [open]);

    /**
     * Loads vendor statuses from the server.
     */
    const loadStatuses = useCallback(async () => {
        setLoading(true);
        const response = await Api.listVendorStatuses();
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            setStatuses((response.data ?? []).map((e) => ({
                ...e,
                inEditMode: false,
                editing: false,
            })));
        }
    }, [isMounted]);

    /**
     * Fetches statuses whenever the dialog opens.
     */
    useEffect(() => {
        if (!open)
            return;
        void loadStatuses();
    }, [loadStatuses, open]);

    /**
     * Closes the dialog, returning the current list unless still loading.
     */
    const closeDialog = useCallback(() => {
        if (busy)
            return;
        onClose(loading ? undefined : statuses.map(({inEditMode, editing, ...rest}) => rest));
    }, [busy, loading, onClose, statuses]);

    const createSchema = useMemo(() => Yup.object({
        name: Yup.string().trim().required(ValidateMessages.required),
    }), []);
    const createValidate = useMemo(() => MuiRFF.makeValidate(createSchema), [createSchema]);
    const createRequired = useMemo(() => MuiRFF.makeRequired(createSchema), [createSchema]);

    /**
     * Creates a new vendor status from the create form.
     */
    const createStatus = useCallback(async (values, form) => {
        if (!canManageAccountInfo) return;
        setCreating(true);
        const response = await Api.addVendorStatus({name: values.name});
        if (!isMounted())
            return;
        setCreating(false);
        if (response?.resultFlag) {
            setStatuses((prev) => [
                ...prev,
                {
                    ...response.data,
                    inEditMode: false,
                    editing: false,
                },
            ]);
            form.change('name', '');
            form.resetFieldState('name');
        }
    }, [canManageAccountInfo, isMounted]);

    /**
     * Updates a status name via UpdateStatus.
     */
    const updateStatus = useCallback(async (status) => {
        if (!canManageAccountInfo) return false;
        setStatuses((prev) => prev.map((e) =>
            e.id === status.id ? {...e, editing: true} : e,
        ));
        const response = await Api.updateVendorStatus({
            id: status.id,
            name: status.name,
            updatedProperties: ['Name'],
        });
        if (!isMounted())
            return false;
        if (response?.resultFlag) {
            setStatuses((prev) => prev.map((e) =>
                e.id === status.id
                    ? {...e, name: status.name, inEditMode: false, editing: false}
                    : e,
            ));
            return true;
        }
        setStatuses((prev) => prev.map((e) =>
            e.id === status.id ? {...e, editing: false} : e,
        ));
        return false;
    }, [canManageAccountInfo, isMounted]);

    /**
     * Sets the given status as the partner default.
     */
    const setStatusAsDefault = useCallback(async (status) => {
        if (!canManageAccountInfo) return;
        if (status.isDefault)
            return;
        setSettingDefaultId(status.id);
        setStatuses((prev) => prev.map((e) =>
            e.id === status.id ? {...e, editing: true} : e,
        ));
        const response = await Api.setDefaultVendorStatus({id: status.id});
        if (!isMounted())
            return;
        setSettingDefaultId(null);
        if (response?.resultFlag) {
            setStatuses((prev) => prev.map((e) => ({
                ...e,
                editing: false,
                isDefault: e.id === status.id,
            })));
        } else {
            setStatuses((prev) => prev.map((e) =>
                e.id === status.id ? {...e, editing: false} : e,
            ));
        }
    }, [canManageAccountInfo, isMounted]);

    /**
     * Opens Redux remove confirmation with replacement options, then deletes.
     */
    const removeStatus = useCallback(async (status) => {
        if (!canManageAccountInfo) return;
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
            open: true,
            data: statuses
                .filter((e) => e.id !== status.id)
                .map((e) => ({
                    title: e.name,
                    id: e.id,
                    data: e,
                })),
            removedEntryTitle: t('removeEntry', {name: status.name}),
            onClose: async (confirmed, replacement) => {
                if (!confirmed)
                    return;
                if (!replacement && statuses.length > 1)
                    return false;

                setStatuses((prev) => prev.map((e) =>
                    e.id === status.id ? {...e, editing: true} : e,
                ));
                const response = await Api.deleteVendorStatus(status.id, replacement.id);
                if (!isMounted())
                    return false;
                if (response?.resultFlag) {
                    setStatuses((prev) => prev.filter((e) => e.id !== status.id));
                } else {
                    setStatuses((prev) => prev.map((e) =>
                        e.id === status.id ? {...e, editing: false} : e,
                    ));
                }
                return response?.resultFlag ?? false;
            },
        }));
    }, [canManageAccountInfo, dispatch, isMounted, statuses, t]);

    /**
     * Toggles inline edit mode for a status card.
     */
    const toggleInEditMode = useCallback((status, inEditMode = false) => {
        setStatuses((prev) => prev.map((e) =>
            e.id === status.id ? {...e, inEditMode} : e,
        ));
    }, []);

    const boxMixin = useCallback((theme) => ({
        backgroundColor: theme.palette.background.tertiary,
        borderRadius: 1,
        padding: 2,
        marginBlock: 2,
    }), []);

    return (
        <Dialog
            open={open}
            setOpen={(nextOpen) => {
                if (nextOpen === false)
                    closeDialog();
            }}
            maxWidth={'sm'}
            fullWidth
            loading={busy}
            disableBackdropClick={busy}
            disableEscapeKeyDown={busy}
            className={'manage-vendor-statuses-dialog'}
        >
            <DialogTitle fontWeight={600}>
                {t('title')}
            </DialogTitle>
            <DialogContent dividers>
                <Form
                    onSubmit={createStatus}
                    validate={createValidate}
                    initialValues={{name: ''}}
                >
                    <Box sx={boxMixin}>
                        <Typography variant={'subtitle2'} fontWeight={600}>
                            {t('createTitle')}
                        </Typography>
                        <Grid2 container spacing={2} sx={{marginBlockStart: 1}}>
                            <Grid2 xs>
                                <MuiRFF.TextField
                                    fullWidth
                                    size={'small'}
                                    name={'name'}
                                    label={t('name')}
                                    required={createRequired.name}
                                    disabled={creating || loading}
                                />
                            </Grid2>
                            <Grid2 xs={'auto'} sx={{display: 'flex', alignItems: 'center'}}>
                                <Button
                                    variant={'contained'}
                                    type={'submit'}
                                    color={'primary'}
                                    size={'medium'}
                                    disabled={creating || loading}
                                >
                                    {creating ? t('creating') : t('create')}
                                </Button>
                            </Grid2>
                        </Grid2>
                    </Box>
                </Form>

                <Box sx={{display: 'flex', flexDirection: 'column', gap: 1.5, marginBlockStart: 1}}>
                    {
                        loading &&
                        Array.from({length: 4}).map((_, key) => (
                            <Skeleton
                                key={key}
                                width={'100%'}
                                height={68}
                                sx={{transform: 'none', minHeight: 68}}
                            />
                        ))
                    }
                    {
                        !loading && statuses.map((status, _, array) => (
                            <StatusCard
                                key={status.id}
                                status={status}
                                update={updateStatus}
                                remove={removeStatus}
                                setDefault={setStatusAsDefault}
                                setInEditMode={toggleInEditMode}
                                removable={array.length > 1 && !status.isDefault}
                                settingDefault={settingDefaultId === status.id}
                                settingDefaultAny={!!settingDefaultId}
                            />
                        ))
                    }
                    {
                        !loading && statuses.length < 1 &&
                        <Typography
                            variant={'caption'}
                            color={'text.secondary'}
                            textAlign={'center'}
                            sx={{marginBlock: 4, width: '100%'}}
                        >
                            {t('empty')}
                        </Typography>
                    }
                </Box>
            </DialogContent>
            <DialogActions className={'p-0'}>
                <Box sx={{
                    margin: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-start',
                    width: '100%',
                }}>
                    <Button
                        variant={'dialog'}
                        color={'primary'}
                        onClick={closeDialog}
                        disabled={busy}
                        borderRadius={'0 8px 0 8px'}
                    >
                        {t('close')}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

/**
 * Single status row: view / edit / default / delete.
 */
const StatusCard = ({
                        status,
                        update,
                        remove,
                        setDefault,
                        setInEditMode,
                        removable,
                        settingDefault,
                        settingDefaultAny,
                    }) => {
    const {t} = useTranslation('dialogs', {keyPrefix: 'app-specific.manage-vendor-statuses'});

    const schema = useMemo(() => Yup.object({
        name: Yup.string().trim().required(ValidateMessages.required),
    }), []);
    const validate = useMemo(() => MuiRFF.makeValidate(schema), [schema]);
    const required = useMemo(() => MuiRFF.makeRequired(schema), [schema]);

    const initialValues = useMemo(() => ({
        name: status?.name ?? '',
    }), [status?.name]);

    /**
     * Saves the renamed status or exits edit mode when unchanged.
     */
    const onSubmit = useCallback(async (values) => {
        if (values.name === status.name) {
            setInEditMode(status, false);
            return;
        }
        await update({...status, name: values.name});
    }, [setInEditMode, status, update]);

    /**
     * Exits edit mode and restores the form values.
     */
    const onRollback = useCallback((form) => {
        setInEditMode(status, false);
        form.reset(initialValues);
    }, [initialValues, setInEditMode, status]);

    return (
        <Card
            variant={'outlined'}
            className={classnames('manage-vendor-statuses-dialog-card', {
                editing: status.inEditMode || status.editing,
            })}
            sx={{paddingInline: 2, paddingBlock: 1.5}}
        >
            <Form
                onSubmit={onSubmit}
                validate={validate}
                initialValues={initialValues}
            >
                {({form}) => (
                    <Grid2 container spacing={1} alignItems={'center'}>
                        <Grid2 xs>
                            <MuiRFF.TextField
                                fullWidth
                                size={'small'}
                                name={'name'}
                                label={t('name')}
                                required={required.name}
                                className={classnames({'show-as-text': !status.inEditMode})}
                                disabled={status.editing || settingDefaultAny}
                            />
                        </Grid2>
                        <Grid2 xs={'auto'}>
                            {
                                !status.inEditMode &&
                                <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                    <Tooltip title={status.isDefault ? t('isDefault') : t('setDefault')}>
                                        <span>
                                            <IconButton
                                                color={'paper.warning'}
                                                disabled={status.editing || settingDefaultAny}
                                                onPressed={() => setDefault(status)}
                                            >
                                                {
                                                    settingDefault
                                                        ? <CircularProgress size={20}/>
                                                        : status.isDefault
                                                            ? <Star sx={{padding: 0.25}}/>
                                                            : <StarBorder sx={{padding: 0.25}}/>
                                                }
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title={t('edit')}>
                                        <span>
                                            <IconButton
                                                color={'paper.primary'}
                                                disabled={status.editing || settingDefaultAny}
                                                onPressed={() => setInEditMode(status, true)}
                                            >
                                                <EditOutlined/>
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Fade in={removable}>
                                        <span>
                                            <Tooltip title={t('delete')}>
                                                <span>
                                                    <IconButton
                                                        color={'paper.error'}
                                                        disabled={!removable || status.editing || settingDefaultAny}
                                                        onPressed={() => remove(status)}
                                                    >
                                                        <DeleteOutlined/>
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </span>
                                    </Fade>
                                </Box>
                            }
                            {
                                status.inEditMode &&
                                <SaveOrRollback
                                    column={false}
                                    show
                                    loading={status.editing}
                                    onRollback={() => onRollback(form)}
                                    onSave={() => form.submit()}
                                />
                            }
                        </Grid2>
                    </Grid2>
                )}
            </Form>
        </Card>
    );
};

export default ManageVendorStatusesDialog;
